import React, { useEffect, useState } from "react";
import { browserHistory } from "react-router";
import PropTypes from "prop-types";
import PageHeader from "../page-header/page-header";
import ScheduleByWeek from "../schedule-by-week/schedule-by-week";
import util from "../util/util";
import _ from "lodash";

export default function App(props) {
  // data about a user;
  const [userData, setUserData] = useState({});
  // using this to preserve the user data on Station change
  const [stationChanged, setStationChanged] = useState(false);
  // indicator for when the page has loaded
  const [userSignedOut, setUserSignedOut] = useState(false);

  // !!! Note, for the future, on how the flow works:
  // 1. when a function component is loaded, it will read all its variables
  //    and functions, and will execute the 'return' part.
  // 2. if the 'return' part also contains loading some sub-components, it will
  //    parse all the 'return' part (so all those sub-components), and at the end,
  //    it will start to load them, one by one.
  // 3. first sub-component is loaded (it will do all the parts from #1.), and:
  // 3.1. if that has any sub-components itself, it will do #2., #3. and so on...
  // 3.2. if it doesn't have, that sub-component it's considered 'mounted' and
  //      it will enter the useEffect function.
  // 4. useEffect of the first sub-component, (after reading all its variables and
  //    functions) it will do the logic in there, and:
  // 4.1. if that logic involves updating the 'state', it will parce all
  //      the 'setData, set...' statements, and at the end, it will re-render
  //      that sub-component entirely (read all v&f + the code from the 'return' part),
  // Notes: - the re-rendering only runs IF any of the state variables changed
  //          (no matter if they are used somewhere in the 'return' part or not)
  //        - the useEffect ONLY gets triggered IF any of the state variables changed
  //          AND IF the variable that changed is included in the dependencyArray
  // 4.2. if that logic contains some async methods (fetch from backend,...)
  //      it will finish here
  // 5. useEffect of the initial component, (after reading all its variables and
  //    functions) it will do the logic in there, and:
  // 5.1. & 5.2. same as #4.1. and #4.2.
  // Note: if it's #5.1., then all the #1.-#4. parts will be run again
  //       (so, each time a parent component is re-rendered (and it re-runs
  //       the 'return' part), its sub-components get re-run from scratch !!!).
  // 6. the async functions get executed (in some order), which could trigger
  //    any combinations of re-rendering (#2.-#5.), for few/multiple times.
  // Note: in contrast with #4.1. and #5.1. (where all the 'set<State>' are
  //       executed sequentially, and at the end ONLY, the component is re-rendered
  //       (and the 'return' part is executed), in the 'then/fail' of an async function,
  //       the flow is different:
  //       each time the state is updated ('setUser', 'setStation',...), it will
  //       re-render the component (and execute the 'return' part)
  //       (ex: updating 5 states, will trigger the re-rendering 5 times,
  //       and not 1 time like in #4.1. and #5.1.)
  //       (the Notes from #4.1. apply also in this case...)
  //       !!! important here: leave the 'loaded' (setLoaded(true);) variable at the end
  //       when updating the state, so that the component is not rendered so many times
  //
  // If we take an example, A -> B -> C,D this will be the flow:
  // - parse A, parse B, parse C, parse D
  // - execute useEffect for D, then for C, then for B, and then for A
  // - depending on the code in useEffect for each, some of them will get re-rendered,
  //   the async methods will be postponed (until all the useEffects have finished...)
  // - and from here the re-rendering will complicate a lot...
  //   or not (if all the changes are architecture correctly)
  //
  // Note on the case when we want to reload the current page, with an updated url (with
  // different query params), but we want to keep the existing components on the page (
  // and only update them, instead of reloading them, and the whole page, from scratch):
  // - if we have only one component, it's easy, but if there are parent and child
  //   components, there can be issues about how to pass the state between them, as
  //   updating the state only in the child component, and then reloading the page (with
  //   different query params), will not update the state in the parent component
  //   directly, so we need to pass the new state to the parent component.
  // - it's true that the parent component will take the new props from the url, but due
  //   to the way hooks work (execute the 'return' part and load any child components in
  //   there, and only after that execute the useEffect (and update it's state)), the
  //   child component gets rendered first with the old state, and only after that with
  //   the updated state, so we're getting that 'flickering' bad user experience (
  //   new state (from child component) -> old state (from parent component) -> back to
  //   new state (from parent component again)).
  // - while if we pass the new state to the parent component, and its state gets
  //   updated, when the child components are reloaded first (before entering the
  //   useEffect), will be reloaded with the correct values...

  useEffect(() => {
    // Note about the sign-in process:
    // the Sign-In and the Sign-Out work in this case (without implementing
    // any functions (like the 'setCurrentStation' one)) because their button uses
    // an <a> tag, instead of the normal 'React Link' component - the difference is:
    // <a>        - will reload the page in the browser, so the whole React App gets
    //              re-rendered from scratch
    // React Link - will reload the page but INSIDE the React App
    // (so, if those <a>'s would be changed to React Link all the Sign-In/Out logic
    // from here (the App component) should be changed...to something more complicated)

    // get the user object (logged in or not)
    const getUser = () => {
      util
        .getUser()
        .fail(() => {
          // user is not logged in yet or has logged out
          // still allow the user to see the page and access the login button
          setUserSignedOut(true);
        })
        .then(data => {
          // user is logged in;
          setUserData(data);

          // Preselect station for user
          const currentStationId = data.current_station?.id || null;
          const localStorageStationId = JSON.parse(localStorage.getItem("stationId"));
          if (!currentStationId && data.stations.length === 1) {
            setCurrentStation(data.stations[0].id);
          } else if (!currentStationId && localStorageStationId) {
            setCurrentStation(localStorageStationId);
          } else if (currentStationId && !localStorageStationId) { // Handle stale localStorage
            localStorage.setItem('stationId', currentStationId);
          }

        });
    };

    if ((_.isEmpty(userData) || stationChanged) && !userSignedOut) {
      // updating the user data, if it's empty (initial load) or if the Station changed
      // but only if the user hasn't manually signed out
      getUser();
      // only updating this state variable if needed (this one triggered the getUser())
      stationChanged && setStationChanged(false);
    }
  }, [props, userData, stationChanged, userSignedOut]);

  // fire when the user selects a new station;
  const setCurrentStation = id => {
    util.saveStation(id).then(() => {
      if (id > 0 && id !== JSON.parse(localStorage.getItem("stationId"))) {
        localStorage.setItem('stationId', id);
      }
      setStationChanged(true);
      //browserHistory.push(browserHistory.getCurrentLocation());
      browserHistory.push(`/`);

      // Note: the user object gets refreshed due to the 'props' change that trigger
      // the 'useEffect' again ('props' change due to 'browserHistory.push(`/`);')
      // so, no need to call 'getUser()' again
    });
  };

  // if a current_station isn't defined, don't let the user see anything beyond the header;
  const renderChildren = () => {
    // station is not set;
    if (!userData.current_station) {
      return null;

      // station is set;
    } else {
      // but the user hasn't gone anywhere yet (homepage);
      if (props.children === null) {
        return <ScheduleByWeek />;

        // otherwise show the selected route components;
      } else {
        return props.children;
      }
    }
  };

  if (_.isEmpty(userData)) {
    return <PageHeader />;
  } else {
    return (
      <div>
        <PageHeader
          userData={userData}
          onChangeStation={id => setCurrentStation(id)}
        />

        {renderChildren()}
      </div>
    );
  }
}

App.propTypes = {
  children: PropTypes.node,
  location: PropTypes.object
};
